import React, { useState, useEffect } from 'react';
import { Palette, Copy, Check, Download, Upload, Sparkles } from 'lucide-react';
import {
  generateColorPalette,
  generatePaletteOptions,
  paletteToCSS,
  paletteToJSON,
  ColorPalette,
  PaletteOption
} from '../../utils/colorPaletteGenerator';
import toast from 'react-hot-toast';

interface ColorPaletteGeneratorProps {
  onSavePalette?: (palette: ColorPalette) => void;
  initialColor?: string;
}

type PaletteMode = 'auto' | 'manual';

const ColorPaletteGenerator: React.FC<ColorPaletteGeneratorProps> = ({
  onSavePalette,
  initialColor = '#3B82F6'
}) => {
  const [mode, setMode] = useState<PaletteMode>('auto');
  const [primaryColor, setPrimaryColor] = useState(initialColor);
  const [paletteOptions, setPaletteOptions] = useState<PaletteOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<PaletteOption | null>(null);
  const [copiedColor, setCopiedColor] = useState<string | null>(null);

  // Manual color inputs
  const [manualColors, setManualColors] = useState<ColorPalette>({
    primary: '#3B82F6',
    primaryLight: '#60A5FA',
    primaryDark: '#2563EB',
    secondary: '#8B5CF6',
    secondaryLight: '#A78BFA',
    secondaryDark: '#7C3AED',
    accent: '#F59E0B',
    accentLight: '#FBBF24',
    accentDark: '#D97706',
    neutral: '#6B7280',
    neutralLight: '#E5E7EB',
    neutralDark: '#374151',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    textPrimary: '#111827',
    textSecondary: '#6B7280',
    background: '#FFFFFF',
    surface: '#F9FAFB'
  });

  // Generate palette options when primary color changes
  useEffect(() => {
    const options = generatePaletteOptions(primaryColor);
    setPaletteOptions(options);
    setSelectedOption(options[0]); // Default to Analogous
  }, [primaryColor]);

  // Handle color input change
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrimaryColor(e.target.value);
  };

  // Copy color to clipboard
  const copyColor = (color: string, label: string) => {
    navigator.clipboard.writeText(color);
    setCopiedColor(color);
    toast.success(`Copied ${label}: ${color}`);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  // Export as CSS
  const exportAsCSS = () => {
    if (!selectedOption) return;
    const css = paletteToCSS(selectedOption.palette);
    const blob = new Blob([css], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'color-palette.css';
    a.click();
    toast.success('CSS file downloaded!');
  };

  // Export as JSON
  const exportAsJSON = () => {
    if (!selectedOption) return;
    const json = paletteToJSON(selectedOption.palette);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'color-palette.json';
    a.click();
    toast.success('JSON file downloaded!');
  };

  // Save palette (callback to parent)
  const handleSavePalette = () => {
    if (mode === 'auto' && selectedOption && onSavePalette) {
      onSavePalette(selectedOption.palette);
      toast.success('Color palette saved!');
    } else if (mode === 'manual' && onSavePalette) {
      onSavePalette(manualColors);
      toast.success('Custom color palette saved!');
    }
  };

  // Update manual color
  const updateManualColor = (colorKey: keyof ColorPalette, value: string) => {
    setManualColors(prev => ({ ...prev, [colorKey]: value }));
  };

  // Load preset brand colors
  const loadPreset = (presetName: string) => {
    const presets: Record<string, ColorPalette> = {
      google: {
        primary: '#4285F4',
        primaryLight: '#669DF6',
        primaryDark: '#1967D2',
        secondary: '#EA4335',
        secondaryLight: '#EE675C',
        secondaryDark: '#C5221F',
        accent: '#FBBC04',
        accentLight: '#FDD663',
        accentDark: '#F29900',
        neutral: '#5F6368',
        neutralLight: '#E8EAED',
        neutralDark: '#3C4043',
        success: '#34A853',
        warning: '#FBBC04',
        error: '#EA4335',
        textPrimary: '#202124',
        textSecondary: '#5F6368',
        background: '#FFFFFF',
        surface: '#F8F9FA'
      },
      microsoft: {
        primary: '#0078D4',
        primaryLight: '#50E6FF',
        primaryDark: '#005A9E',
        secondary: '#00BCF2',
        secondaryLight: '#00D7F0',
        secondaryDark: '#008272',
        accent: '#FFB900',
        accentLight: '#FFF100',
        accentDark: '#D83B01',
        neutral: '#737373',
        neutralLight: '#E1DFDD',
        neutralDark: '#323130',
        success: '#107C10',
        warning: '#FFB900',
        error: '#D83B01',
        textPrimary: '#201F1E',
        textSecondary: '#737373',
        background: '#FFFFFF',
        surface: '#FAF9F8'
      },
      facebook: {
        primary: '#1877F2',
        primaryLight: '#4A97F7',
        primaryDark: '#0C5BBE',
        secondary: '#42B72A',
        secondaryLight: '#69CA55',
        secondaryDark: '#2D9419',
        accent: '#FF6B6B',
        accentLight: '#FF8989',
        accentDark: '#E85555',
        neutral: '#65676B',
        neutralLight: '#E4E6EB',
        neutralDark: '#050505',
        success: '#42B72A',
        warning: '#FFB900',
        error: '#FF6B6B',
        textPrimary: '#1C1E21',
        textSecondary: '#65676B',
        background: '#FFFFFF',
        surface: '#F0F2F5'
      }
    };

    if (presets[presetName]) {
      setManualColors(presets[presetName]);
      toast.success(`${presetName.charAt(0).toUpperCase() + presetName.slice(1)} brand colors loaded!`);
    }
  };

  // Extract color from uploaded image (placeholder - would need color extraction library)
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      toast.loading('Extracting colors from image...');
      // TODO: Implement image color extraction using a library like 'color-thief'
      // For now, just show a message
      setTimeout(() => {
        toast.dismiss();
        toast('Color extraction from images coming soon!', { icon: '🎨' });
      }, 1000);
    }
  };

  // Render a color swatch
  const ColorSwatch: React.FC<{ color: string; label: string; size?: 'small' | 'large' }> = ({
    color,
    label,
    size = 'small'
  }) => {
    const isCopied = copiedColor === color;
    const sizeClass = size === 'large' ? 'h-20 w-20' : 'h-12 w-12';

    return (
      <div className="flex flex-col items-center gap-2">
        <button
          onClick={() => copyColor(color, label)}
          className={`${sizeClass} rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-all cursor-pointer relative group shadow-sm hover:shadow-md`}
          style={{ backgroundColor: color }}
          title={`Click to copy ${color}`}
        >
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded-lg">
            {isCopied ? (
              <Check className="w-5 h-5 text-white" />
            ) : (
              <Copy className="w-5 h-5 text-white" />
            )}
          </div>
        </button>
        <div className="text-center">
          <p className="text-xs font-medium text-gray-700">{label}</p>
          <p className="text-xs text-gray-500 font-mono">{color}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-3 rounded-lg">
            <Palette className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Client Color Palette Generator</h2>
            <p className="text-sm text-gray-500">
              {mode === 'auto' ? 'Generate harmonious brand colors from a single primary color' : 'Manually define your exact brand colors'}
            </p>
          </div>
        </div>
        <Sparkles className="w-6 h-6 text-purple-500 animate-pulse" />
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setMode('auto')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'auto'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          🪄 Auto Generate
        </button>
        <button
          onClick={() => setMode('manual')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'manual'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          ✏️ Manual Entry
        </button>
      </div>

      {/* Manual Mode - Brand Presets */}
      {mode === 'manual' && (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <span>✨</span> Quick Start: Brand Presets
          </h3>
          <p className="text-sm text-gray-600">Load pre-configured colors from popular brands</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => loadPreset('google')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
            >
              <span className="text-2xl">🔵</span>
              <span className="font-medium">Google</span>
            </button>
            <button
              onClick={() => loadPreset('microsoft')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
            >
              <span className="text-2xl">⊞</span>
              <span className="font-medium">Microsoft</span>
            </button>
            <button
              onClick={() => loadPreset('facebook')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
            >
              <span className="text-2xl">f</span>
              <span className="font-medium">Facebook</span>
            </button>
          </div>
        </div>
      )}

      {/* Manual Mode - Color Inputs */}
      {mode === 'manual' && (
        <div className="space-y-6">
          {/* Primary Colors */}
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Primary Colors</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['primary', 'primaryLight', 'primaryDark'].map((colorKey) => (
                <div key={colorKey}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="h-10 w-10 rounded border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Secondary Colors */}
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Secondary Colors</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['secondary', 'secondaryLight', 'secondaryDark'].map((colorKey) => (
                <div key={colorKey}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="h-10 w-10 rounded border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Accent Colors */}
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Accent Colors</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['accent', 'accentLight', 'accentDark'].map((colorKey) => (
                <div key={colorKey}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="h-10 w-10 rounded border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status Colors */}
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Status Colors</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['success', 'warning', 'error'].map((colorKey) => (
                <div key={colorKey}>
                  <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                    {colorKey}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="h-10 w-10 rounded border-2 border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={manualColors[colorKey as keyof ColorPalette]}
                      onChange={(e) => updateManualColor(colorKey as keyof ColorPalette, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Auto Mode - Primary Color Input */}
      {mode === 'auto' && (
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <span>🎨</span> Primary Brand Color
        </h3>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Hex Color or Use Picker
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={primaryColor}
                onChange={handleColorChange}
                className="h-12 w-12 rounded-lg border-2 border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={handleColorChange}
                placeholder="#3B82F6"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Or Extract from Image
            </label>
            <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <Upload className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-700">Upload Logo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
      )}

      {/* Auto Mode - Palette Options Tabs */}
      {mode === 'auto' && (
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Color Harmony Options</h3>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {paletteOptions.map((option) => (
            <button
              key={option.name}
              onClick={() => setSelectedOption(option)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                selectedOption?.name === option.name
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.name}
            </button>
          ))}
        </div>
        {selectedOption && (
          <p className="text-sm text-gray-600 mt-2 italic">{selectedOption.description}</p>
        )}
      </div>
      )}

      {/* Auto Mode - Color Palette Display */}
      {mode === 'auto' && selectedOption && (
        <div className="space-y-6">
          {/* Primary Colors */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Primary Colors</h4>
            <div className="flex justify-around">
              <ColorSwatch color={selectedOption.palette.primaryLight} label="Light" />
              <ColorSwatch color={selectedOption.palette.primary} label="Primary" size="large" />
              <ColorSwatch color={selectedOption.palette.primaryDark} label="Dark" />
            </div>
          </div>

          {/* Secondary Colors */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Secondary Colors</h4>
            <div className="flex justify-around">
              <ColorSwatch color={selectedOption.palette.secondaryLight} label="Light" />
              <ColorSwatch color={selectedOption.palette.secondary} label="Secondary" size="large" />
              <ColorSwatch color={selectedOption.palette.secondaryDark} label="Dark" />
            </div>
          </div>

          {/* Accent Colors */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Accent Colors</h4>
            <div className="flex justify-around">
              <ColorSwatch color={selectedOption.palette.accentLight} label="Light" />
              <ColorSwatch color={selectedOption.palette.accent} label="Accent" size="large" />
              <ColorSwatch color={selectedOption.palette.accentDark} label="Dark" />
            </div>
          </div>

          {/* Neutral Colors */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Neutral Colors</h4>
            <div className="flex justify-around">
              <ColorSwatch color={selectedOption.palette.neutralLight} label="Light" />
              <ColorSwatch color={selectedOption.palette.neutral} label="Neutral" />
              <ColorSwatch color={selectedOption.palette.neutralDark} label="Dark" />
            </div>
          </div>

          {/* Status Colors */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Status Colors</h4>
            <div className="flex justify-around">
              <ColorSwatch color={selectedOption.palette.success} label="Success" />
              <ColorSwatch color={selectedOption.palette.warning} label="Warning" />
              <ColorSwatch color={selectedOption.palette.error} label="Error" />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={handleSavePalette}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg font-medium"
        >
          <Check className="w-4 h-4" />
          Save Palette
        </button>

        <button
          onClick={exportAsCSS}
          className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          <Download className="w-4 h-4" />
          Export as CSS
        </button>

        <button
          onClick={exportAsJSON}
          className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          <Download className="w-4 h-4" />
          Export as JSON
        </button>
      </div>

      {/* Preview Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-800 mb-4">Preview in Use</h4>
        {((mode === 'auto' && selectedOption) || mode === 'manual') && (
          <div className="space-y-3">
            {/* Header preview */}
            <div
              className="rounded-lg p-4 text-white"
              style={{ backgroundColor: mode === 'auto' ? selectedOption?.palette.primary : manualColors.primary }}
            >
              <h3 className="text-xl font-bold">Sample Report Header</h3>
              <p className="text-sm opacity-90">Generated with your brand colors</p>
            </div>

            {/* Section preview */}
            <div className="bg-white rounded-lg p-4 border-l-4" style={{ borderColor: mode === 'auto' ? selectedOption?.palette.accent : manualColors.accent }}>
              <h4 className="font-semibold" style={{ color: mode === 'auto' ? selectedOption?.palette.primary : manualColors.primary }}>
                Section Title
              </h4>
              <p className="text-sm text-gray-600 mt-2">
                This is how your content will look with the selected color palette applied.
              </p>
            </div>

            {/* Button preview */}
            <div className="flex gap-3">
              <button
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: mode === 'auto' ? selectedOption?.palette.primary : manualColors.primary }}
              >
                Primary Button
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: mode === 'auto' ? selectedOption?.palette.secondary : manualColors.secondary }}
              >
                Secondary Button
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPaletteGenerator;
